import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { LineChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import {
    Activity, Phone, UserPlus, TrendingUp, RefreshCw, Search, Clock,
    GraduationCap, Loader2, AlertCircle,
} from 'lucide-react';
import { apiClient, useAuth } from '@/lib/api';
import { useSearchParams } from 'react-router-dom';
import logger from '@/lib/logger';

const POLL_MS = 30_000; // 30s — real-time enough for managers without hammering the API
const SCOPE_OPTIONS = [
    { value: 'sales', label: 'Sales' },
    { value: 'cs', label: 'Customer Service' },
    { value: 'mentor', label: 'Mentor' },
    { value: 'bd', label: 'Business Dev' },
];

// Map user role → which scopes they're allowed to switch to.
// Sales TL/SM/Exec can only see sales; CS only sees CS; Mentor only mentor; etc.
// Super admin can see them all.
const allowedScopesForRole = (role) => {
    if (['super_admin', 'admin'].includes(role)) {
        return ['sales', 'cs', 'mentor', 'bd'];
    }
    if (['sales_manager', 'team_leader', 'sales_executive'].includes(role)) return ['sales'];
    if (['cs_head', 'cs_agent'].includes(role)) return ['cs'];
    if (['master_of_academics', 'mentor'].includes(role)) return ['mentor'];
    if (['business_development'].includes(role)) return ['bd'];
    return ['sales'];
};

const fmtMinutes = (mins) => {
    if (mins == null) return '—';
    if (mins === 0) return '0m';
    if (mins < 1) return '<1m';
    if (mins < 60) return `${Math.round(mins)}m`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m}m`;
};

const fmtAED = (n) => `AED ${Math.round(n || 0).toLocaleString()}`;

// Tiny sparkline — last 7 days. Two overlaid series (leads + enrolled) so
// managers can spot a sudden slump or surge without leaving the page.
const Sparkline = ({ leads = [], enrolled = [], days = [], agentName = '' }) => {
    const data = useMemo(() => {
        const len = Math.max(leads.length, enrolled.length, 7);
        return Array.from({ length: len }, (_, i) => ({
            day: days[i] || `D${i - len + 1}`,
            leads: leads[i] ?? 0,
            enrolled: enrolled[i] ?? 0,
        }));
    }, [leads, enrolled, days]);
    const hasData = leads.some(v => v > 0) || enrolled.some(v => v > 0);
    if (!hasData) {
        return <div className="text-[10px] text-muted-foreground/60 italic h-7 flex items-center justify-end">no activity</div>;
    }
    return (
        <div className="h-7 w-24 ml-auto" data-testid={`sparkline-${agentName.replace(/\s+/g, '-').toLowerCase()}`}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line
                        type="monotone"
                        dataKey="leads"
                        stroke="#3b82f6"
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="enrolled"
                        stroke="#10b981"
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                    />
                    <RechartsTooltip
                        content={({ active, payload, label }) => {
                            if (!active || !payload || !payload.length) return null;
                            const leadsVal = payload.find(p => p.dataKey === 'leads')?.value ?? 0;
                            const enrolledVal = payload.find(p => p.dataKey === 'enrolled')?.value ?? 0;
                            return (
                                <div className="bg-popover border rounded shadow-sm px-2 py-1 text-[11px]">
                                    <div className="font-medium">{label}</div>
                                    <div className="text-blue-500">Leads: {leadsVal}</div>
                                    <div className="text-emerald-500">Enrolled: {enrolledVal}</div>
                                </div>
                            );
                        }}
                        cursor={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = 'text-blue-500', testid }) => (
    <Card className="p-4 flex items-start gap-3" data-testid={testid}>
        <div className={`rounded-lg p-2 bg-muted ${accent}`}><Icon className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold leading-tight tabular-nums">{value}</div>
            {sub != null && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    </Card>
);

const SortHeader = ({ label, col, sortBy, sortDir, onSort, align = 'left' }) => (
    <th
        onClick={() => onSort(col)}
        className={`cursor-pointer select-none py-2 px-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors text-${align}`}
        data-testid={`prod-sort-${col}`}
    >
        {label}
        {sortBy === col && <span className="ml-1 opacity-60">{sortDir === 'desc' ? '↓' : '↑'}</span>}
    </th>
);

export default function ProductivityDashboardPage() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const allowedScopes = useMemo(() => allowedScopesForRole(user?.role), [user?.role]);
    // Pre-select scope from ?scope=cs URL param when it's an allowed scope,
    // so the dedicated "CS Productivity Live" sidebar link lands on the CS view.
    const initialScope = (() => {
        const q = searchParams.get('scope');
        if (q && allowedScopes.includes(q)) return q;
        return allowedScopes[0] || 'sales';
    })();
    const [scope, setScope] = useState(initialScope);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [err, setErr] = useState('');
    const [search, setSearch] = useState('');
    const [teamFilter, setTeamFilter] = useState('all');
    const [sortBy, setSortBy] = useState('leads_received_today');
    const [sortDir, setSortDir] = useState('desc');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const isStrictTL = user?.role === 'team_leader';
    const canFilterByTeam = ['sales_manager', 'admin', 'super_admin', 'cs_head', 'master_of_academics'].includes(user?.role);

    const pollRef = useRef(null);

    const fetchData = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            else setRefreshing(true);
            const params = new URLSearchParams({ scope });
            if (canFilterByTeam && teamFilter !== 'all') params.append('team_id', teamFilter);
            const res = await apiClient.get(`/productivity/realtime?${params.toString()}`);
            setData(res.data);
            setLastUpdated(new Date());
            setErr('');
        } catch (e) {
            logger.error('productivity fetch failed', e);
            setErr(e?.response?.data?.detail || 'Failed to load productivity data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [scope, teamFilter, canFilterByTeam]);

    // Initial + on-change fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh loop
    useEffect(() => {
        if (!autoRefresh) {
            if (pollRef.current) clearInterval(pollRef.current);
            return;
        }
        pollRef.current = setInterval(() => fetchData(true), POLL_MS);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [autoRefresh, fetchData]);

    const handleSort = (col) => {
        if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(col); setSortDir('desc'); }
    };

    // Build distinct team list from agents themselves (no extra API call)
    const teamOptions = useMemo(() => {
        const seen = new Map();
        for (const a of (data?.agents || [])) {
            if (a.team_id && !seen.has(a.team_id)) {
                seen.set(a.team_id, a.team_name || a.team_id.slice(0, 8));
            }
        }
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }, [data]);

    const filteredAgents = useMemo(() => {
        let rows = data?.agents || [];
        // Strict TL → already scoped by backend; client-side filter is a no-op safety net
        if (isStrictTL && user?.team_id) {
            rows = rows.filter(a => !a.team_id || a.team_id === user.team_id);
        }
        // Team filter (managers only)
        if (canFilterByTeam && teamFilter !== 'all') {
            rows = rows.filter(a => a.team_id === teamFilter);
        }
        // Search by name
        const s = search.trim().toLowerCase();
        if (s) rows = rows.filter(a => (a.full_name || '').toLowerCase().includes(s));
        // Sort
        rows = [...rows].sort((a, b) => {
            const av = a[sortBy];
            const bv = b[sortBy];
            // Null TTFC → push to end
            if (av == null && bv == null) return (a.full_name || '').localeCompare(b.full_name || '');
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'string') {
                return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
            }
            return sortDir === 'desc' ? bv - av : av - bv;
        });
        return rows;
    }, [data, search, teamFilter, isStrictTL, user?.team_id, canFilterByTeam, sortBy, sortDir]);

    const totals = data?.totals || {};

    return (
        <div className="space-y-6" data-testid="productivity-dashboard-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Activity className="h-6 w-6 text-emerald-500" />
                        Real-time Agent Productivity
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Auto-refreshes every 30s · Today (UTC)
                        {lastUpdated && (
                            <span className="ml-2" data-testid="last-updated">
                                · Updated {lastUpdated.toLocaleTimeString()}
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {allowedScopes.length > 1 && (
                        <Select value={scope} onValueChange={setScope}>
                            <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="prod-scope">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SCOPE_OPTIONS.filter(s => allowedScopes.includes(s.value)).map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button
                        variant={autoRefresh ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAutoRefresh(v => !v)}
                        data-testid="prod-toggle-auto"
                    >
                        <Activity className={`h-4 w-4 mr-1 ${autoRefresh ? 'animate-pulse' : ''}`} />
                        {autoRefresh ? 'Live' : 'Paused'}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchData()}
                        disabled={refreshing}
                        data-testid="prod-refresh"
                    >
                        <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard
                    icon={UserPlus}
                    label="Leads received"
                    value={totals.leads_received ?? 0}
                    sub={`${totals.active_agents ?? 0} of ${totals.total_agents ?? 0} agents active`}
                    accent="text-blue-500"
                    testid="kpi-leads-received"
                />
                <KpiCard
                    icon={Activity}
                    label="Touched today"
                    value={totals.touched_today ?? 0}
                    accent="text-violet-500"
                    testid="kpi-touched"
                />
                <KpiCard
                    icon={Phone}
                    label="Calls made"
                    value={totals.calls_made ?? 0}
                    sub={`${fmtMinutes(totals.call_minutes)} on-call`}
                    accent="text-amber-500"
                    testid="kpi-calls"
                />
                <KpiCard
                    icon={Clock}
                    label="Avg time-to-first-contact"
                    value={fmtMinutes(totals.avg_ttfc_minutes)}
                    accent="text-rose-500"
                    testid="kpi-ttfc"
                />
                <KpiCard
                    icon={GraduationCap}
                    label="Enrolled today"
                    value={totals.enrolled_today ?? 0}
                    accent="text-emerald-500"
                    testid="kpi-enrolled"
                />
                <KpiCard
                    icon={TrendingUp}
                    label="Revenue today"
                    value={fmtAED(totals.enrolled_revenue)}
                    accent="text-emerald-600"
                    testid="kpi-revenue"
                />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search agents..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 w-64 h-9"
                        data-testid="prod-search"
                    />
                </div>
                {canFilterByTeam && teamOptions.length > 0 && (
                    <Select value={teamFilter} onValueChange={setTeamFilter}>
                        <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="prod-team-filter">
                            <SelectValue placeholder="All Teams" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Teams</SelectItem>
                            {teamOptions.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Badge variant="secondary" data-testid="prod-row-count">
                    {filteredAgents.length} agent{filteredAgents.length === 1 ? '' : 's'}
                </Badge>
            </div>

            {/* Agent table */}
            <Card className="overflow-hidden">
                {err && (
                    <div className="flex items-center gap-2 p-4 bg-destructive/5 text-destructive text-sm border-b" data-testid="prod-error">
                        <AlertCircle className="h-4 w-4" />
                        {err}
                    </div>
                )}
                {loading && !data ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="prod-table">
                            <thead className="bg-muted/40 border-b">
                                <tr>
                                    <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Agent</th>
                                    <SortHeader label="Leads" col="leads_received_today" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Touched" col="touched_today" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Calls" col="calls_made_today" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Talk Time" col="call_minutes" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="TTFC" col="avg_ttfc_minutes" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Moved w/o Call" col="moved_without_call_today" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Enrolled" col="enrolled_today" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Revenue" col="enrolled_revenue" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground whitespace-nowrap">
                                        7-day
                                        <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-normal">
                                            <span className="inline-block h-1.5 w-2 rounded-sm bg-blue-500" /> leads
                                            <span className="inline-block h-1.5 w-2 rounded-sm bg-emerald-500 ml-1" /> enrolled
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAgents.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="text-center py-10 text-muted-foreground text-sm" data-testid="prod-empty">
                                            No agents to show.
                                        </td>
                                    </tr>
                                ) : filteredAgents.map(a => {
                                    const idle = a.touched_today === 0 && a.calls_made_today === 0;
                                    const gaming = (a.moved_without_call_today || 0) > 0;
                                    return (
                                        <tr
                                            key={a.agent_id}
                                            className={`border-b last:border-0 transition-colors ${
                                                gaming ? 'bg-amber-500/10 hover:bg-amber-500/15' : 'hover:bg-muted/20'
                                            }`}
                                            data-testid={`prod-row-${a.agent_id}`}
                                            title={gaming ? `${a.moved_without_call_today} lead${a.moved_without_call_today === 1 ? '' : 's'} moved out of "new" today without any call — possible SLA gaming` : undefined}
                                        >
                                            <td className="py-2 px-3">
                                                <div className="flex flex-col">
                                                    <span className={`font-medium ${idle ? 'text-muted-foreground' : ''}`}>{a.full_name}</span>
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {(a.role || '').replace(/_/g, ' ')}
                                                        {a.team_name && ` · ${a.team_name}`}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-2 px-3 text-right tabular-nums">{a.leads_received_today}</td>
                                            <td className="py-2 px-3 text-right tabular-nums">{a.touched_today}</td>
                                            <td className="py-2 px-3 text-right tabular-nums">{a.calls_made_today}</td>
                                            <td className="py-2 px-3 text-right tabular-nums">{fmtMinutes(a.call_minutes)}</td>
                                            <td className="py-2 px-3 text-right tabular-nums">{fmtMinutes(a.avg_ttfc_minutes)}</td>
                                            <td className="py-2 px-3 text-right tabular-nums" data-testid={`prod-moved-nocall-${a.agent_id}`}>
                                                {gaming ? (
                                                    <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/30">
                                                        ⚠ {a.moved_without_call_today}
                                                    </Badge>
                                                ) : <span className="text-muted-foreground">0</span>}
                                            </td>
                                            <td className="py-2 px-3 text-right tabular-nums">
                                                {a.enrolled_today > 0 ? (
                                                    <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600">
                                                        {a.enrolled_today}
                                                    </Badge>
                                                ) : <span className="text-muted-foreground">0</span>}
                                            </td>
                                            <td className="py-2 px-3 text-right tabular-nums">{fmtAED(a.enrolled_revenue)}</td>
                                            <td className="py-2 px-3">
                                                <Sparkline
                                                    leads={a.trend_leads_7d || []}
                                                    enrolled={a.trend_enrolled_7d || []}
                                                    days={data?.trend_days || []}
                                                    agentName={a.full_name}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
